/**
 * Z-depth selection cycling — Blender-style click-through.
 *
 * When a user clicks an area of the viewport with several overlapping
 * selectable items, a single click should select the frontmost (nearest
 * camera). Clicking the same spot again should advance through the stack
 * from front to back, wrapping around at the end. This lets users reach
 * occluded items without having to orbit.
 *
 * "Selectable items" is a small union today — scene objects and the 3D
 * cursor — modelled as `CycleTarget`. The cycle machinery doesn't care
 * which is which; it just walks the stack. Target-specific dispatch
 * lives in `performCycleSelection`, so adding a new selectable kind in
 * the future is a one-liner there and a matching walk-up rule here.
 *
 * State is module-level on purpose: the cursor is a singleton, this
 * state is pure ephemeral input, and routing every click through Zustand
 * would just add noise.
 *
 * Cycle state is ephemeral — callers should invoke `resetSelectionCycle()`
 * whenever the selection is cleared, an empty-space click is handled, or
 * anything else that would make the previous stack stale.
 */

import {
  type ObjectSelectionTarget,
  objectSelectionTargetKey,
  resolveStoreKeySelectionTarget,
} from "../app/objectSelection.ts";
import { useStore } from "../app/store.ts";
import { CYCLE_CLICK_DIST_SQ, CYCLE_CLICK_MS } from "./interactionConstants.ts";

/**
 * Sentinel `Object3D.name` used on the 3D cursor's group so the cycle
 * resolver can pick it out of an intersection list without needing a
 * direct import or ref into the cursor gizmo component.
 */
export const CURSOR3D_HIT_NAME = "__poseref_cursor3d";

export type CycleTarget =
  | { kind: "selection"; target: ObjectSelectionTarget }
  | { kind: "cursor" };

type CycleState = {
  lastX: number;
  lastY: number;
  lastTime: number;
  /** Snapshot of targets under the original click, nearest-first. */
  stack: CycleTarget[];
  /** Index of the currently-selected entry within `stack`. */
  index: number;
};

const state: CycleState = {
  lastX: 0,
  lastY: 0,
  lastTime: 0,
  stack: [],
  index: 0,
};

/** Clear the cycle stack so the next click starts fresh at the frontmost hit. */
export function resetSelectionCycle(): void {
  state.lastTime = 0;
  state.stack = [];
  state.index = 0;
}

/**
 * Record the position of a click that did not resolve through the cycle
 * path (e.g. additive Shift+click, blend-entry selection, double-tap).
 *
 * Updating the cursor position without seeding a stack prevents a
 * subsequent cycle-advance from firing off stale data while still letting
 * the position-tolerance check behave sensibly.
 */
export function noteNonCycleClick(clientX: number, clientY: number): void {
  resetSelectionCycle();
  state.lastX = clientX;
  state.lastY = clientY;
  state.lastTime = performance.now();
}

type IntersectionLike = {
  object: { name?: string; parent?: unknown | null } | null;
};

/**
 * Walk an ordered list of Three.js intersections into a depth-ordered
 * list of unique selectable targets.
 *
 * R3F delivers `event.intersections` nearest-first, but each entry may
 * be a nested child (a skinned sub-mesh, a light's invisible hit proxy,
 * the cursor's transparent hit quad). We climb the parent chain until we
 * find an ancestor whose `name` either matches a registered SceneObject
 * id or is the cursor sentinel, deduplicating so each target only
 * appears once in the stack.
 */
function collectCycleTargets(
  intersections: readonly IntersectionLike[] | undefined,
): CycleTarget[] {
  if (!intersections || intersections.length === 0) return [];
  const sceneObjects = useStore.getState().sceneObjects;
  const blendFiles = useStore.getState().blendFiles;
  const collections = useStore.getState().collections;
  const result: CycleTarget[] = [];
  const seenIds = new Set<string>();
  let sawCursor = false;
  for (const hit of intersections) {
    let obj: any = hit.object;
    while (obj) {
      const n: string | undefined = obj.name;
      if (n === CURSOR3D_HIT_NAME) {
        if (!sawCursor) {
          sawCursor = true;
          result.push({ kind: "cursor" });
        }
        break;
      }
      if (n && sceneObjects.has(n)) {
        const target = resolveStoreKeySelectionTarget(
          blendFiles,
          collections,
          n,
        );
        const targetKey = objectSelectionTargetKey(target);
        if (!seenIds.has(targetKey)) {
          seenIds.add(targetKey);
          result.push({ kind: "selection", target });
        }
        break;
      }
      obj = obj.parent;
    }
  }
  return result;
}

function isTargetStillValid(target: CycleTarget): boolean {
  if (target.kind === "cursor") return true;
  const state = useStore.getState();
  switch (target.target.kind) {
    case "sceneObject":
      return state.sceneObjects.has(target.target.id);
    case "blendEntry":
      return state.blendFiles.has(target.target.id);
    case "collection":
      return state.collections.has(target.target.id);
  }
}

/**
 * Resolve a click into the target that should be selected.
 *
 * If the click is close enough (in space and time) to the previous one
 * and the stored stack has more than one entry, the stack index advances
 * and the next target is returned. Otherwise a fresh stack snapshot is
 * taken from `intersections`, the cursor is recorded, and the frontmost
 * target is returned.
 *
 * `fallback` is used when the intersection walk-up produces no matches
 * — which shouldn't happen for handlers fired from their own meshes,
 * but defends against hand-built hierarchies that bypass the renderer.
 */
function resolveClickTarget(
  clientX: number,
  clientY: number,
  intersections: readonly IntersectionLike[] | undefined,
  fallback: CycleTarget,
): CycleTarget {
  const now = performance.now();
  const ddx = clientX - state.lastX;
  const ddy = clientY - state.lastY;

  // Re-validate the stored stack against the live scene — an object
  // could have been deleted or renamed between clicks. A transient miss
  // collapses the cycle and forces a fresh snapshot.
  if (state.stack.length > 0) {
    for (const t of state.stack) {
      if (!isTargetStillValid(t)) {
        resetSelectionCycle();
        break;
      }
    }
  }

  const withinWindow =
    state.stack.length > 1 &&
    now - state.lastTime < CYCLE_CLICK_MS &&
    ddx * ddx + ddy * ddy < CYCLE_CLICK_DIST_SQ;

  if (withinWindow) {
    state.index = (state.index + 1) % state.stack.length;
    state.lastTime = now;
    return state.stack[state.index]!;
  }

  const targets = collectCycleTargets(intersections);
  state.stack = targets;
  state.index = 0;
  state.lastX = clientX;
  state.lastY = clientY;
  state.lastTime = now;
  return targets[0] ?? fallback;
}

/**
 * Dispatch a resolved cycle target to the correct store action. The
 * discriminated-union switch is the single place in the codebase that
 * knows how to select each kind of cycle target — adding a new kind
 * (e.g. bone, light probe) is a one-line addition here.
 */
function selectCycleTarget(target: CycleTarget): void {
  const store = useStore.getState();
  switch (target.kind) {
    case "cursor":
      store.setCursor3DSelected(true);
      return;
    case "selection":
      store.selectObjectTarget(target.target, false);
      return;
  }
}

/**
 * Resolve + dispatch in one call. Every tap handler in the viewport
 * funnels through this so cycling "just works" regardless of which
 * component's pointerup handler R3F happens to fire — cube-in-front-of-
 * cursor and cursor-in-front-of-cube both take the same path.
 */
export function performCycleSelection(
  clientX: number,
  clientY: number,
  intersections: readonly IntersectionLike[] | undefined,
  fallback: CycleTarget,
): void {
  selectCycleTarget(
    resolveClickTarget(clientX, clientY, intersections, fallback),
  );
}
